import { useState } from 'react';

import { Check, CheckCircle, CloseRounded, Visibility } from '@mui/icons-material';
import LoadingButton from '@mui/lab/LoadingButton';
import { Button, Link, Typography } from '@mui/material';
import {
    AddMerchantApplication,
    ApplicationStatus,
    ServiceStatus,
    ServiceType,
} from '@prisma/client';

import NPIButton from 'src/components/Button';
import Chip from 'src/components/Chip';
import { useAuthContext } from 'src/contexts/AuthProvider';
import ErrorDetailsModal from 'src/pages/MerchantApplicationPage/components/ErrorDetailsModal';
import SendApplicationSignatureModal from 'src/pages/MerchantApplicationPage/components/SendApplicationSignatureModal';
import { canFlagErrorIdentifiedMerchantApplication } from 'src/pages/MerchantApplicationPage/merchantApplicationHelpers.util';

import BorderedCard from '../BorderedCard/BorderedCard';
import { Flex } from '../Flex';

import { UpdateStatusButton } from './components/UpdateStatusButton';

interface SignatureStatusProps {
    label: string;
    status: ApplicationStatus;
    onApplicationStatusUpdate: (status: ApplicationStatus) => void;
    serviceApplication: AddMerchantApplication;
    handleCompleteOrder: (status: ApplicationStatus) => void;
    processingRequestBuild?: boolean;
    handleProcessServiceBuild: ({ terminalId }: { terminalId: string }) => void;
    terminalId?: string;
    mid?: string;
    handleContinueClick: () => void;
    agreementPreviewUrl?: string;
    isRefreshing?: boolean;
}

const SignatureStatus = ({
    label,
    status,
    onApplicationStatusUpdate,
    serviceApplication,
    handleCompleteOrder,
    processingRequestBuild,
    handleProcessServiceBuild,
    terminalId,
    mid,
    handleContinueClick,
    agreementPreviewUrl,
    isRefreshing,
}: SignatureStatusProps) => {
    const { isAdmin, isPartner } = useAuthContext();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isResendModalOpen, setIsResendModalOpen] = useState(false);

    const handleOpenModal = () => {
        return setIsModalOpen(true);
    };
    const handleCloseModal = () => {
        return setIsModalOpen(false);
    };

    const canFlagErrorIdentified = !canFlagErrorIdentifiedMerchantApplication(
        isAdmin,
        { status },
        isPartner
    );
    const showRequestBuildButton = status === ApplicationStatus.NPI_INTERNAL_CS_REVIEW;

    const showErrorResolvedButton = status === ApplicationStatus.ERROR_IDENTIFIED;

    const showCompleteOrderButton = status === ApplicationStatus.AWAITING_HARDWARE_DISPATCH;

    return (
        <BorderedCard
            flexDirection="row"
            sx={{
                padding: '15px',
                '@media (max-width: 600px)': {
                    flexDirection: 'column',
                    padding: '10px',
                },
            }}
        >
            <Flex
                alignItems="center"
                sx={{
                    flexDirection: { xs: 'column', sm: 'row' },
                    margin: '0 10px 10px 10px',
                }}
            >
                <Typography
                    fontWeight="500"
                    fontSize="24px"
                    lineHeight="34px"
                    color="#1D1D1B"
                    marginRight="16px"
                >
                    {label}:
                </Typography>
                <Chip label={(status ?? '').replace(/_/g, ' ')} chipcolor="orange" />
            </Flex>
            <Flex
                sx={{
                    display: 'flex',
                    gap: '20px',
                    '@media (max-width: 768px)': {
                        flexDirection: 'column',
                        gap: '10px',
                    },
                }}
            >
                {status === ApplicationStatus.DRAFT ? (
                    <Button variant="npi-primary" onClick={handleContinueClick}>
                        continue application
                    </Button>
                ) : (
                    <Flex gap="20px">
                        <SendApplicationSignatureModal
                            isModalOpen={isResendModalOpen}
                            handleCloseModal={() => {
                                return setIsResendModalOpen(false);
                            }}
                            application={serviceApplication}
                        />
                        {status === ApplicationStatus.EXPIRED && (
                            <Button
                                variant="npi-primary"
                                onClick={() => {
                                    return setIsResendModalOpen(true);
                                }}
                            >
                                Resend for Signature
                            </Button>
                        )}
                        <Link href={agreementPreviewUrl} target="_blank">
                            <Button
                                disabled={isRefreshing}
                                startIcon={<Visibility />}
                                variant="npi-primary"
                                sx={{ p: 1 }}
                            >
                                View
                            </Button>
                        </Link>
                        {showRequestBuildButton && (
                            <LoadingButton
                                startIcon={<CheckCircle />}
                                variant="npi-primary"
                                type="button"
                                sx={{ color: 'white' }}
                                loading={processingRequestBuild}
                                disabled={
                                    isRefreshing ||
                                    (serviceApplication?.serviceRequirements?.service?.serviceType === ServiceType.TERMINAL
                                        ? !terminalId
                                        : !mid)
                                }
                                onClick={() => {
                                    return handleProcessServiceBuild({ terminalId });
                                }}
                            >
                                Request Build
                            </LoadingButton>
                        )}
                        {showCompleteOrderButton && (
                            <UpdateStatusButton
                                handleClick={handleCompleteOrder}
                                title="Complete Order"
                                status={ServiceStatus.ACTIVE}
                                icon={<Check />}
                                disabled={isRefreshing}
                            />
                        )}
                        {showErrorResolvedButton && (
                            <UpdateStatusButton
                                handleClick={onApplicationStatusUpdate}
                                title="Resolve Errors Now"
                                status={ApplicationStatus.DRAFT}
                                icon={<CloseRounded />}
                                disabled={isRefreshing}
                            />
                        )}
                        <NPIButton
                            hidden={canFlagErrorIdentified}
                            startIcon={<CloseRounded />}
                            onClick={handleOpenModal}
                            label="Error Identified"
                            disabled={isRefreshing}
                        />
                        <ErrorDetailsModal
                            isModalOpen={isModalOpen}
                            handleCloseModal={handleCloseModal}
                            merchantApplication={serviceApplication}
                            onApplicationStatusUpdate={onApplicationStatusUpdate}
                        />
                    </Flex>
                )}
            </Flex>
        </BorderedCard>
    );
};

export default SignatureStatus;
